import { jsx, jsxs } from "react/jsx-runtime";
import { useRef } from "react";
import { B as Box } from "./Grid-CcQKdsgp.js";
import { useTableCell, useTableSelectionCheckbox, useTableColumnHeader, useTableHeaderRow, useTableRow, useTableRowGroup, useTableSelectAllCheckbox, useTable } from "@react-aria/table";
import { useFocusRing } from "@react-aria/focus";
import { mergeProps } from "@react-aria/utils";
import { C as Checkbox } from "./Checkbox-DQv1WUai.js";
import { F as Flex } from "./Flex-BqkSIn_m.js";
import { VisuallyHidden } from "@react-aria/visually-hidden";
import { useTableState, Cell, Column, Row, TableBody, TableHeader } from "@react-stately/table";
const DEFAULT_SPACING_PADDING = [12, 8, 12, 8];
const TableCell = function(props) {
  var _a;
  const ref = useRef(null);
  const { cell, state, ...indentationProps } = props;
  const { gridCellProps } = useTableCell({ node: cell }, state, ref);
  const { isFocusVisible, focusProps } = useFocusRing();
  return /* @__PURE__ */ jsx(
    Box,
    {
      ...mergeProps(gridCellProps, focusProps),
      ...indentationProps,
      display: "table-cell",
      p: (_a = indentationProps.p) != null ? _a : DEFAULT_SPACING_PADDING,
      className: `outline-none ${isFocusVisible ? "shadow-[inset_0_0_0_2px_orange]" : "shadow-none"}`,
      ref,
      as: "td",
      style: {
        ...indentationProps.style
      },
      children: cell.rendered
    }
  );
};
const TableCheckboxCell = ({
  cell,
  state
}) => {
  const ref = useRef(null);
  const { gridCellProps } = useTableCell({ node: cell }, state, ref);
  if (!cell.parentKey) {
    return null;
  }
  const { checkboxProps } = useTableSelectionCheckbox(
    { key: cell.parentKey },
    state
  );
  return /* @__PURE__ */ jsx("td", { ...gridCellProps, ref, children: /* @__PURE__ */ jsx(Box, { p: { px: 8, py: 12 }, md: { p: { py: 12, px: 16 } }, children: /* @__PURE__ */ jsx(
    Checkbox,
    {
      "aria-label": "label",
      ...checkboxProps,
      value: state.selectionManager.isSelected(cell.key)
    }
  ) }) });
};
const TableColumnHeader = (props) => {
  var _a, _b, _c;
  const { column, state, md, d, ...indentationProps } = props;
  const ref = useRef(null);
  const { columnHeaderProps } = useTableColumnHeader(
    { node: column },
    state,
    ref
  );
  const { isFocusVisible, focusProps } = useFocusRing();
  const arrowIcon = ((_a = state.sortDescriptor) == null ? void 0 : _a.direction) === "ascending" ? "▲" : "▼";
  return /* @__PURE__ */ jsx(
    "th",
    {
      ...mergeProps(columnHeaderProps, focusProps),
      colSpan: column.colspan,
      className: ` [font-size:var(--fontSize-mobile-small-body)] [line-height:var(--lineHeight-mobile-small-body)] [font-weight:var(--fontWeight-mobile-semibold)] md:[font-size:var(--fontSize-desktop-small-body)] md:[line-height:var(--lineHeight-desktop-small-body)] text-[var(--colors-text-secondary-default)] outline-none cursor-default ${((_b = column.colspan) != null ? _b : 0 > 1) ? "text-center" : "text-left"} ${isFocusVisible && "shadow-[inset_0_0_0_2px_var(--colors-surface-delete)]"}  `,
      ref,
      children: /* @__PURE__ */ jsxs(
        Box,
        {
          style: {
            ...indentationProps.style
          },
          ...indentationProps,
          md,
          d,
          children: [
            column.rendered,
            column.props.allowsSorting && /* @__PURE__ */ jsx(
              Flex,
              {
                p: { px: 2 },
                as: "span",
                "aria-hidden": "true",
                className: `${((_c = state.sortDescriptor) == null ? void 0 : _c.column) === column.key ? "visible" : "invisible"}`,
                children: arrowIcon
              }
            )
          ]
        }
      )
    }
  );
};
const TableHeaderRow = ({
  item,
  state,
  children
}) => {
  const ref = useRef(null);
  const { rowProps } = useTableHeaderRow({ node: item }, state, ref);
  return /* @__PURE__ */ jsx("tr", { className: "bg-[rgba(155,95,245,0.25)]", ...rowProps, ref, children });
};
const TableRow = ({ item, children, state }) => {
  const ref = useRef(null);
  const isSelected = state.selectionManager.isSelected(item.key);
  const { rowProps } = useTableRow(
    {
      node: item
    },
    state,
    ref
  );
  const { focusProps } = useFocusRing();
  return /* @__PURE__ */ jsx(
    Box,
    {
      width: "100%",
      display: "table-row",
      as: "tr",
      fontSize: "body.mobile.small",
      lineHeight: "body.mobile.small",
      md: { fontSize: "body.desktop.small", lineHeight: "body.desktop.small" },
      ...mergeProps(rowProps, focusProps),
      bg: isSelected ? "surface-primary-subtle" : item.index % 2 ? "surface-neutral-default" : "surface-neutral-dark-default",
      style: { userSelect: "all", ...{ important: "user-select" } },
      ref,
      children
    }
  );
};
const TableRowGroup = ({
  type: Element,
  children
}) => {
  const { rowGroupProps } = useTableRowGroup();
  return /* @__PURE__ */ jsx(Element, { ...rowGroupProps, children });
};
const TableSelectAllCell = ({
  column,
  state
}) => {
  const ref = useRef(null);
  const { columnHeaderProps } = useTableColumnHeader(
    { node: column },
    state,
    ref
  );
  const { checkboxProps } = useTableSelectAllCheckbox(state);
  return /* @__PURE__ */ jsx("th", { ...columnHeaderProps, ref, children: state.selectionManager.selectionMode === "single" ? /* @__PURE__ */ jsx(VisuallyHidden, { children: checkboxProps["aria-label"] }) : /* @__PURE__ */ jsx(
    Checkbox,
    {
      className: "px-2 py-3 md:p-4",
      "aria-label": "label",
      ...checkboxProps,
      value: checkboxProps.isSelected
    }
  ) });
};
const Table = function(props) {
  const { selectionMode, selectionBehavior, className, ...indentationProps } = props;
  const state = useTableState({
    ...props,
    showSelectionCheckboxes: selectionMode === "multiple" && selectionBehavior !== "replace"
  });
  const ref = useRef(null);
  const { collection } = state;
  const { gridProps } = useTable(indentationProps, state, ref);
  return /* @__PURE__ */ jsxs(
    Box,
    {
      as: "table",
      display: "table",
      className: `rounded-lg overflow-hidden w-full ${className != null ? className : ""}`,
      ...indentationProps,
      ...gridProps,
      style: {
        ...indentationProps.style
      },
      ref,
      children: [
        /* @__PURE__ */ jsx(TableRowGroup, { type: "thead", children: collection.headerRows.map((headerRow) => /* @__PURE__ */ jsx(TableHeaderRow, { item: headerRow, state, children: [...headerRow.childNodes].map(
          (column) => column.props.isSelectionCell ? /* @__PURE__ */ jsx(
            TableSelectAllCell,
            {
              column,
              state
            },
            column.key
          ) : /* @__PURE__ */ jsx(
            TableColumnHeader,
            {
              column,
              state,
              md: { m: 16 }
            },
            column.key
          )
        ) }, headerRow.key)) }),
        /* @__PURE__ */ jsx(TableRowGroup, { type: "tbody", children: [...collection.body.childNodes].map((row) => /* @__PURE__ */ jsx(TableRow, { item: row, state, children: [...row.childNodes].map(
          (cell) => cell.props.isSelectionCell ? /* @__PURE__ */ jsx(TableCheckboxCell, { cell, state }, cell.key) : /* @__PURE__ */ jsx(
            TableCell,
            {
              cell,
              state,
              md: { p: { py: 12, px: 16 } }
            },
            cell.key
          )
        ) }, row.key)) })
      ]
    }
  );
};
Table.Cell = Cell;
Table.Column = Column;
Table.Row = Row;
Table.Body = TableBody;
Table.Header = TableHeader;
export {
  Table as T
};
